import type { ApiClient, FieldSubject } from '@carbonwebmail/api-client'
import type { ContactFieldDto } from '@carbonwebmail/contracts'
import { useCallback, useState } from 'react'

/**
 * Declaring a field, wherever somebody does it.
 *
 * Two screens offer it — the Fields tab and the `+` at the right edge of the
 * board — and they are the same act with two shapes around it. One view model
 * so there is one answer to what a draft is, what the key means when it is left
 * blank, and what each refusal says: two would be two places for "that key is
 * taken" to be worded differently, and the one somebody hit would be the one
 * that had drifted.
 *
 * The key is left to the server unless somebody chooses one. It is what every
 * saved rule and every merge token names, so it must be possible to pick
 * deliberately — and equally, most people should never have to think about it.
 */

export interface FieldDraft {
  readonly label: string
  readonly key: string
  readonly type: ContactFieldDto['type']
  /** One per line, which is how somebody types a short list. */
  readonly choices: string
}

const EMPTY: FieldDraft = { label: '', key: '', type: 'text', choices: '' }

export function useDeclareField({
  client,
  subject,
  onDeclared,
}: {
  readonly client: ApiClient
  /** Which board's columns these are. Decides only where the row lands. */
  readonly subject: FieldSubject
  /** Reloads whatever list the caller is showing. */
  readonly onDeclared: () => Promise<void> | void
}) {
  const [draft, setDraft] = useState<FieldDraft>(EMPTY)
  const [problem, setProblem] = useState<string | undefined>(undefined)
  const [saving, setSaving] = useState(false)

  const change = useCallback(<K extends keyof FieldDraft>(key: K, value: FieldDraft[K]) => {
    setProblem(undefined)
    setDraft((current) => ({ ...current, [key]: value }))
  }, [])

  const declare = useCallback(async () => {
    setSaving(true)
    setProblem(undefined)
    try {
      const outcome = await client.declareField(subject, {
        label: draft.label,
        // Omitted rather than sent empty, so the request says what it means:
        // "you choose".
        ...(draft.key === '' ? {} : { key: draft.key }),
        type: draft.type,
        // Blank lines are what typing a list looks like half-finished, not a
        // choice somebody meant.
        choices: draft.choices
          .split('\n')
          .map((choice) => choice.trim())
          .filter((choice) => choice !== ''),
      })

      if (outcome.status === 'key-taken') {
        setProblem('A field with that key already exists.')
        return false
      }
      if (outcome.status === 'invalid') {
        setProblem(outcome.problem)
        return false
      }

      setDraft(EMPTY)
      await onDeclared()
      return true
    } catch {
      setProblem('We could not add that field. Please try again in a moment.')
      return false
    } finally {
      setSaving(false)
    }
  }, [client, draft, onDeclared, subject])

  return { draft, problem, saving, change, declare }
}
